import React, { useState, useEffect, useMemo } from "react";
import { AppShell, Box, useMantineTheme } from "@mantine/core";
import { useLocation } from "react-router-dom";
import NavBar from "../NavBar/NavBar";
import { NAV_RAIL_WIDTH } from "../NavBar/NavRail";
import { useSalon } from "../../context/SalonContext";
import { useMediaQuery } from "@mantine/hooks";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import TrialBanner from "../common/TrialBanner";
import PaymentRequiredModal from "../common/PaymentRequiredModal";
import { SchedulingFocusProvider } from "../../context/SchedulingFocusContext";

function AppLayout({ children }) {
  // Full-screen scheduling focus (Reschedule / Rebook) hides the nav bar.
  const [schedulingFocus, setSchedulingFocus] = useState(false);
  const schedulingFocusValue = useMemo(
    () => ({ focus: schedulingFocus, setFocus: setSchedulingFocus }),
    [schedulingFocus],
  );
  const { onboardingStage } = useSalon();
  const { error: accessError } = useFeatureAccess();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const location = useLocation();

  // State for payment required modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalDetails, setPaymentModalDetails] = useState({});

  // Show payment modal when we get a 402 error
  useEffect(() => {
    if (accessError?.type === "payment_required") {
      setPaymentModalOpen(true);
      setPaymentModalDetails(accessError.details || {});
    }
  }, [accessError]);

  // Hide navbar for website generator wizard, or while a full-screen
  // scheduling surface (Reschedule / Rebook) is focused.
  const hideNavbar =
    location.pathname.includes("/website-generator-wizard") || schedulingFocus;
  // The mobile calendar draws its own header band + nav drawer, so the app's
  // 53px mobile top bar (and the padding reserved for it) steps aside there.
  const calendarOwnsMobileChrome =
    isMobile && location.pathname.startsWith("/admin/calendar");

  return (
    <SchedulingFocusProvider value={schedulingFocusValue}>
      {/* Payment Required Modal - blocks entire app when shown */}
      <PaymentRequiredModal
        opened={paymentModalOpen}
        message={accessError?.message}
        trialEndDt={paymentModalDetails?.trial_end_dt}
        subscriptionStatus={paymentModalDetails?.subscription_status}
      />

      <AppShell
        navbar={
          !isMobile && !hideNavbar
            ? { width: NAV_RAIL_WIDTH, breakpoint: "sm" }
            : undefined
        }
        padding={0}
        styles={{
          main: {
            paddingTop:
              isMobile && !calendarOwnsMobileChrome
                ? "var(--app-mobile-topbar-height)"
                : 0,
            backgroundColor: hideNavbar ? "#ffffff" : theme.colors.gray[0],
          },
        }}
      >
        {!isMobile && !hideNavbar && (
          <AppShell.Navbar>
            <NavBar onboardingStage={onboardingStage} />
          </AppShell.Navbar>
        )}

        {isMobile && !hideNavbar && !calendarOwnsMobileChrome && (
          <NavBar onboardingStage={onboardingStage} />
        )}

        <AppShell.Main>
          <Box>
            {/* Trial Banner - shows warning for trial users */}
            <TrialBanner />
            {children}
          </Box>
        </AppShell.Main>
      </AppShell>
    </SchedulingFocusProvider>
  );
}

export default AppLayout;
